import React, { useEffect, useState, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [state, setState] = useState({ phase: 'checking', data: null, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: 'error', data: null, error: 'Missing session_id' });
      return;
    }
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const data = res.data;
        if (data.payment_status === 'paid') {
          setState({ phase: 'success', data, error: null });
          return;
        }
        if (data.status === 'expired') {
          setState({ phase: 'expired', data, error: null });
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ phase: 'pending', data, error: null });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (err) {
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setState({ phase: 'error', data: null, error: err?.response?.data?.detail || 'Could not verify payment' });
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <Navbar />
      <main className="flex-1 flex items-center justify-center px-6 pt-24 pb-16">
        <div className="max-w-lg w-full bg-white border border-neutral-200 rounded-3xl shadow-sm p-10 text-center">
          {state.phase === 'checking' && (
            <>
              <Loader2 className="w-12 h-12 text-neutral-400 animate-spin mx-auto" />
              <h1 className="mt-6 font-display text-2xl font-bold text-neutral-900">Confirming your payment…</h1>
              <p className="mt-2 text-neutral-600">Hang tight — this usually takes a few seconds.</p>
            </>
          )}

          {state.phase === 'success' && (
            <>
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="mt-5 font-display text-3xl font-bold text-neutral-900">Payment received</h1>
              <p className="mt-2 text-neutral-600">
                {state.data?.tier === 'credits' ? (
                  <>
                    <span className="font-semibold text-neutral-900">{state.data?.credits_granted} credits</span> have
                    been added to your account.
                  </>
                ) : (
                  <>
                    Welcome to <span className="font-semibold text-neutral-900">Transformerus {state.data?.tier === 'pro' ? 'Pro' : 'Standard'}</span>.
                    Your account is upgraded and ready to build.
                  </>
                )}
              </p>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="bg-neutral-50 border border-neutral-200 rounded-2xl px-4 py-3 text-left">
                  <div className="text-xs text-neutral-500">Amount paid</div>
                  <div className="font-semibold text-neutral-900 text-lg">
                    ${(state.data.amount_total / 100).toFixed(2)} {state.data.currency?.toUpperCase()}
                  </div>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3 text-left">
                  <div className="text-xs text-emerald-700">New credit balance</div>
                  <div className="font-semibold text-emerald-900 text-lg">
                    {state.data.new_balance ?? state.data.credits_granted ?? '—'} credits
                  </div>
                </div>
              </div>
              <Link to="/build" className="block mt-7">
                <Button className="w-full h-12 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-white">
                  <Sparkles className="w-4 h-4 mr-1.5" /> Start building
                  <ArrowRight className="w-4 h-4 ml-1.5" />
                </Button>
              </Link>
            </>
          )}

          {state.phase === 'pending' && (
            <>
              <Loader2 className="w-12 h-12 text-amber-500 animate-spin mx-auto" />
              <h1 className="mt-6 font-display text-2xl font-bold text-neutral-900">Still processing</h1>
              <p className="mt-2 text-neutral-600">
                We haven't received final confirmation from Stripe yet. You'll get an email shortly.
              </p>
              <Link to="/" className="block mt-6">
                <Button variant="outline" className="w-full h-11 rounded-2xl">Back to home</Button>
              </Link>
            </>
          )}

          {state.phase === 'expired' && (
            <>
              <XCircle className="w-12 h-12 text-neutral-400 mx-auto" />
              <h1 className="mt-6 font-display text-2xl font-bold text-neutral-900">Session expired</h1>
              <p className="mt-2 text-neutral-600">Your checkout session expired before payment completed. No money was taken.</p>
              <Link to="/pricing" className="block mt-6">
                <Button className="w-full h-11 rounded-2xl bg-neutral-900 text-white">Try again</Button>
              </Link>
            </>
          )}

          {state.phase === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-rose-500 mx-auto" />
              <h1 className="mt-6 font-display text-2xl font-bold text-neutral-900">Something went wrong</h1>
              <p className="mt-2 text-neutral-600">{state.error}</p>
              <Link to="/pricing" className="block mt-6">
                <Button className="w-full h-11 rounded-2xl bg-neutral-900 text-white">Back to pricing</Button>
              </Link>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
